import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import httpClient from '../api/axios';
import { useDispatch } from 'react-redux';

import {
  setLoginStatus,
  setUserDetails,
  setUserId,
} from '../redux/authSlice';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validate = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await httpClient.post('/api/login', {
        email: formData.email,
        password: formData.password,
      });

      // console.log('Login response:', response.data);

      dispatch(setLoginStatus(true));
      dispatch(setUserDetails(response.data.user));
      dispatch(setUserId(response.data.user._id));

      // Store token or user data in localStorage
      // localStorage.setItem('isLoggedIn', 'true');
      // localStorage.setItem(
      //   'user',
      //   JSON.stringify(response.data.user)
      // );
      // localStorage.setItem('userId', response.data.user._id);

      navigate('/');
    } catch (error) {
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 404) {
          setErrors({
            api: 'Login endpoint not found. Please check the API URL.',
          });
        } else if (error.response.status === 400) {
          setErrors({
            api:
              error.response.data.message ||
              'Invalid email or password',
          });
        } else if (error.response.status === 401) {
          setErrors({
            api: 'Unauthorized. Please check your credentials.',
          });
        } else {
          setErrors({
            api:
              error.response.data.message ||
              'Login failed. Please try again.',
          });
        }
      } else if (error.request) {
        // Request was made but no response received
        setErrors({
          api: 'No response from server. Please check your connection.',
        });
      } else {
        // Other errors
        setErrors({
          api: error.message || 'An error occurred during login.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex flex-col justify-center items-center bg-gray-100 p-6'>
      <div className='bg-white shadow-lg rounded-lg p-10 w-full max-w-md'>
        <h2 className='text-3xl font-bold text-center text-purple-700 mb-4'>
          Login
        </h2>
        <p className='text-center text-gray-500 mb-6'>
          Enter your credentials to access your account.
        </p>

        {errors.api && (
          <p className='text-red-500 text-center mb-4'>
            {errors.api}
          </p>
        )}

        <form onSubmit={handleSubmit} className='space-y-6'>
          <div>
            <input
              type='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500'
              placeholder='Email Address'
            />
            {errors.email && (
              <p className='text-red-500 text-sm mt-1'>
                {errors.email}
              </p>
            )}
          </div>

          <div className='relative'>
            <input
              type={showPassword ? 'text' : 'password'}
              name='password'
              value={formData.password}
              onChange={handleChange}
              className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500'
              placeholder='Password'
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute right-3 top-3 text-gray-500 hover:text-gray-700'
            >
              {showPassword ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                  />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                  />
                </svg>
              )}
            </button>
            {errors.password && (
              <p className='text-red-500 text-sm mt-1'>
                {errors.password}
              </p>
            )}
          </div>

          <button
            type='submit'
            className='w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition duration-300 flex justify-center items-center'
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className='mt-6 text-center'>
          <p className='text-gray-600'>
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/register')}
              className='text-purple-600 hover:text-purple-700 font-semibold'
            >
              Register
            </button>
          </p>
        </div>

        <button
          onClick={() => navigate('/')}
          className='mt-4 text-purple-600 hover:text-purple-700 font-semibold block mx-auto'
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default Login;
